import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import useXeroApi from "../../hooks/useXeroApi";
import WritebackAccountField from "./WritebackAccountField";
import { showToast } from "../../utils/utils";
import { showToastType } from "../../utils/constants/appConstants";
import { accSoftwareTab, commonErrMsg } from "../../locales/en";

AccountingWritebackFields.propTypes = {
  readOnly: PropTypes.bool
};

/**
 * Liability account / Expense account writeback code pickers.
 * Renders directly below "Last Synced" and above the help/CTA block on the
 * Accounting Software tab. Xero-only - the backing endpoints
 * (/onboard/xero/writeback-accounts) only exist for Xero.
 *
 * readOnly mirrors the rest of this tab's secondary-user gating (see
 * AccountingSoftwareTab's primaryUserId handling) - a secondary/beneficial-owner
 * viewer of someone else's account can't change accounting software or disconnect
 * it there, so the writeback pickers must be equally non-interactive for them
 * rather than the only editable control left on this screen.
 */
export default function AccountingWritebackFields({ readOnly }) {
  const { XeroGetWritebackAccounts, XeroUpdateWritebackAccounts } =
    useXeroApi();

  const [liabilityAccounts, setLiabilityAccounts] = useState([]);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [selection, setSelection] = useState({
    liabilityAccountId: "",
    liabilityLabel: "",
    expenseAccountId: "",
    expenseLabel: ""
  });
  const [openField, setOpenField] = useState(null);
  // Per-field, not a single shared value - a shared "which field is saving" flag means
  // starting a save on one field (which sets it to that field) incidentally re-enables
  // the *other* field's trigger even while its own save is still in flight, letting a
  // user fire two overlapping saves for the same field whose responses can then resolve
  // out of order and leave the DB holding the older selection.
  const [savingFields, setSavingFields] = useState({
    liability: false,
    expense: false
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // useXeroApi() returns freshly-created function references on every render
    // (they aren't memoized), so those functions must not be used as effect
    // dependencies - doing so would recompute the callback and re-fire this
    // effect on every render, hammering these endpoints in an infinite loop.
    // Fetch exactly once, on mount.
    let cancelled = false;

    const loadData = async () => {
      try {
        // A single consolidated fetch, not one call per liability/expense/current-
        // selection - those are the public onboarding-flow endpoints and each one
        // writes an "account connected/submitted" audit row, which would be wrong
        // to log just for opening this screen to look at the saved value.
        const response = await XeroGetWritebackAccounts();
        if (cancelled) return;

        const data = response?.data?.data || {};
        setLiabilityAccounts(data.liabilityAccountOptions || []);
        setExpenseAccounts(data.expenseAccountOptions || []);
        setSelection({
          liabilityAccountId: data.liabilityAccount?.accountId || "",
          liabilityLabel: data.liabilityAccount
            ? `${data.liabilityAccount.code} - ${data.liabilityAccount.name}`
            : "",
          expenseAccountId: data.expenseAccount?.accountId || "",
          expenseLabel: data.expenseAccount
            ? `${data.expenseAccount.code} - ${data.expenseAccount.name}`
            : ""
        });
      } catch (error) {
        if (error?.response?.status !== 404) {
          // A 404 means no onboarding record yet (or, in practice, an org that isn't
          // connected - this component doesn't even render in that case) - the fields
          // fall back to their "Select an account" placeholder silently. Anything else
          // (permission, server, network) is surfaced so a missing grant on this
          // endpoint doesn't look identical to "nothing saved yet".
          showToast(
            error?.response?.data?.message || commonErrMsg.item2,
            showToastType.error
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = async (field, option) => {
    if (readOnly) {
      // Defense in depth - the trigger is already non-interactive for a readOnly
      // viewer, but guard the save path itself too rather than rely solely on the
      // UI being disabled.
      return;
    }
    if (option.AccountID === selection[`${field}AccountId`]) {
      // Already the saved value (e.g. opening the dropdown and pressing Enter
      // on the current selection) - nothing changed, so don't PUT.
      return;
    }
    if (savingFields[field]) {
      // A save for this exact field is already in flight - the trigger should be
      // disabled while that's true, but guard here too rather than let a second
      // overlapping save for the same field fire and risk resolving out of order.
      return;
    }

    const uuidKey =
      field === "liability" ? "liabilityAccountUuid" : "expenseAccountUuid";
    const previousAccountId = selection[`${field}AccountId`];
    const previousLabel = selection[`${field}Label`];

    setSavingFields((prev) => ({ ...prev, [field]: true }));
    setSelection((prev) => ({
      ...prev,
      [`${field}AccountId`]: option.AccountID,
      [`${field}Label`]: `${option.Code} - ${option.Name}`
    }));

    try {
      await XeroUpdateWritebackAccounts({ [uuidKey]: option.AccountID });
      showToast(accSoftwareTab?.writebackUpdateSuccess, showToastType.success);
    } catch (error) {
      // Revert only this field, read from current state rather than the
      // closure snapshot - the other field may have saved successfully while
      // this request was in flight, and its result must not be clobbered.
      setSelection((current) => ({
        ...current,
        [`${field}AccountId`]: previousAccountId,
        [`${field}Label`]: previousLabel
      }));
      showToast(
        error?.response?.data?.message || commonErrMsg.item2,
        showToastType.error
      );
    } finally {
      setSavingFields((prev) => ({ ...prev, [field]: false }));
    }
  };

  if (loading) {
    return null;
  }

  return (
    <>
      <WritebackAccountField
        label={accSoftwareTab?.liabilityAccountLabel}
        helperText={accSoftwareTab?.liabilityAccountHelper}
        options={liabilityAccounts}
        value={selection.liabilityAccountId}
        valueLabel={selection.liabilityLabel}
        disabled={savingFields.liability}
        readOnly={readOnly}
        open={openField === "liability"}
        onToggle={() =>
          setOpenField((current) =>
            current === "liability" ? null : "liability"
          )
        }
        onRequestClose={() =>
          setOpenField((current) => (current === "liability" ? null : current))
        }
        onSelect={(option) => handleSelect("liability", option)}
      />
      <WritebackAccountField
        label={accSoftwareTab?.expenseAccountLabel}
        helperText={accSoftwareTab?.expenseAccountHelper}
        options={expenseAccounts}
        value={selection.expenseAccountId}
        valueLabel={selection.expenseLabel}
        disabled={savingFields.expense}
        readOnly={readOnly}
        open={openField === "expense"}
        onToggle={() =>
          setOpenField((current) => (current === "expense" ? null : "expense"))
        }
        onRequestClose={() =>
          setOpenField((current) => (current === "expense" ? null : current))
        }
        onSelect={(option) => handleSelect("expense", option)}
      />
    </>
  );
}
